import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

export type AlertVariant = 'info' | 'success' | 'warning' | 'danger';

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  title?: string;
  children?: ReactNode;
  onDismiss?: () => void;
}

const variantClasses: Record<AlertVariant, string> = {
  info: 'border-secondary/30 bg-secondary/5 text-on-surface',
  success: 'border-success/30 bg-success/5 text-on-surface',
  warning: 'border-warning/30 bg-warning/5 text-on-surface',
  danger: 'border-danger/30 bg-danger/5 text-on-surface',
};

const iconMap: Record<AlertVariant, string> = {
  info: 'ℹ',
  success: '✓',
  warning: '⚠',
  danger: '✕',
};

export function Alert({
  className,
  variant = 'info',
  title,
  children,
  onDismiss,
  ...props
}: AlertProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex gap-3 rounded-lg border px-4 py-3 text-sm',
        variantClasses[variant],
        className,
      )}
      {...props}
    >
      <span className="mt-0.5 shrink-0 font-bold" aria-hidden="true">
        {iconMap[variant]}
      </span>
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && 'mt-1')}>{children}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 text-on-surface-variant hover:text-on-surface"
          aria-label="Cerrar alerta"
        >
          ✕
        </button>
      )}
    </div>
  );
}
